import React, { useState, useEffect } from 'react';
import { AssetDossier, UserAssetAnnotation, StudioSession } from '../../types';
import { buildSuiteCrossUrl } from '../../lib/ghostSession';
import { canExportCleanPdf } from '../../config/accessTiers';
import {
  getCountyRecorderUrl,
  getSecEdgarUrl,
  getStatutoryAuthorityUrl,
  getIsoQueuePortalUrl,
} from '../../lib/sourceLinks';
import {
  X,
  ExternalLink,
  ShieldCheck,
  FileText,
  Building,
  Zap,
  Sliders,
  Download,
  Star,
  Lock,
  Copy,
  Check,
  MapPin,
  Calendar,
  AlertTriangle,
} from 'lucide-react';

interface DossierDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  dossier: AssetDossier | null;
  session: StudioSession;
  annotation: UserAssetAnnotation | null;
  onSaveAnnotation: (annotation: Partial<UserAssetAnnotation>) => Promise<void>;
  onLoadInSandbox: (itMw: number, rentKwMo: number) => void;
  onExportIcMemo: (canonicalId: string) => void;
  onOpenAuthModal: () => void;
}

export const DossierDrawer: React.FC<DossierDrawerProps> = ({
  isOpen,
  onClose,
  dossier,
  session,
  annotation,
  onSaveAnnotation,
  onLoadInSandbox,
  onExportIcMemo,
  onOpenAuthModal,
}) => {
  const [dealStage, setDealStage] = useState(annotation?.deal_stage || 'WATCHLIST');
  const [privateNotes, setPrivateNotes] = useState(annotation?.private_notes || '');
  const [isStarred, setIsStarred] = useState(annotation?.is_starred || false);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [copiedHash, setCopiedHash] = useState<string | null>(null);

  // Sync state when annotation changes
  useEffect(() => {
    if (annotation) {
      setDealStage(annotation.deal_stage || 'WATCHLIST');
      setPrivateNotes(annotation.private_notes || '');
      setIsStarred(annotation.is_starred || false);
    } else {
      setDealStage('WATCHLIST');
      setPrivateNotes('');
      setIsStarred(false);
    }
  }, [annotation, dossier?.canonical_project_id]);

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !dossier) return null;

  const tx = dossier.transaction;
  const decomp = dossier.valuation_decomposition;
  const deeds = dossier.deeds || [];
  const leases = dossier.leases || [];
  const pilots = dossier.pilots || [];
  const radar = dossier.queue_distress;

  const isCleanPdf = canExportCleanPdf(session.tier);

  // Fallbacks for underwriting proxies
  const itLoadMw = tx?.headline_capacity_mw || dossier.capacity_mw || 250;
  const rentRate = leases.length > 0 ? leases[0].rent_rate_usd_kw_month : 185;

  const handleCopyHash = (hash: string) => {
    navigator.clipboard.writeText(hash);
    setCopiedHash(hash);
    setTimeout(() => setCopiedHash(null), 2000);
  };

  const handleSaveNotes = async () => {
    setIsSavingNotes(true);
    try {
      await onSaveAnnotation({
        canonical_project_id: dossier.canonical_project_id,
        deal_stage: dealStage as any,
        private_notes: privateNotes,
        is_starred: isStarred,
        internal_rating: 'TIER_1',
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } finally {
      setIsSavingNotes(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black/60 backdrop-blur-sm flex justify-end animate-fadeIn">
      <div
        className="w-full max-w-2xl lg:max-w-3xl bg-[#0c121e] border-l border-[#1a253b] text-slate-100 h-full overflow-y-auto shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 1. Header */}
        <div className="p-5 border-b border-[#1a253b] bg-[#090e1a] sticky top-0 z-20 space-y-3">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-[#2979ff]/15 text-[#2979ff] border border-[#2979ff]/30 font-bold">
                  {dossier.canonical_project_id}
                </span>
                <span className="text-xs text-slate-400 font-mono">
                  {dossier.county ? `${dossier.county} County, ` : ''}{dossier.state} {dossier.iso_rto ? `• ${dossier.iso_rto}` : ''}
                </span>
              </div>
              <h2 className="text-xl font-bold text-white tracking-tight">
                {dossier.canonical_name}
              </h2>
              {tx?.deal_name && (
                <p className="text-xs text-slate-300">
                  Transaction: <b className="text-white">{tx.deal_name}</b> ({tx.deal_type.replace(/_/g, ' ')})
                </p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsStarred(!isStarred)}
                className={`p-2 rounded-lg border transition ${
                  isStarred
                    ? 'bg-amber-500/20 text-amber-400 border-amber-500/40'
                    : 'bg-[#121a2d] border-[#1a253b] text-slate-400 hover:text-white'
                }`}
                title={isStarred ? 'Starred Asset' : 'Star Asset'}
              >
                <Star className={`w-4 h-4 ${isStarred ? 'fill-amber-400' : ''}`} />
              </button>

              <button
                onClick={onClose}
                className="p-2 rounded-lg text-slate-400 hover:text-white bg-[#121a2d] hover:bg-[#1a253b] border border-[#1a253b] transition"
                title="Close Drawer (Esc)"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Action Row */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-[#1a253b]/80">
            {/* Quick Actions */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  onLoadInSandbox(itLoadMw, rentRate);
                  onClose();
                }}
                className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-[#2979ff] hover:bg-[#1c68e8] text-white font-semibold text-xs transition shadow-sm"
              >
                <Sliders className="w-3.5 h-3.5" />
                <span>Underwrite in Sandbox</span>
              </button>

              <button
                onClick={() => onExportIcMemo(dossier.canonical_project_id)}
                className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-[#121a2d] hover:bg-[#1a253b] text-slate-200 border border-[#1a253b] text-xs font-semibold transition"
              >
                <Download className="w-3.5 h-3.5 text-[#2979ff]" />
                <span>IC Memo (PDF)</span>
                {!isCleanPdf && <span className="text-[10px] text-slate-400">(Preview)</span>}
              </button>
            </div>

            {/* Sister App Cross-Launch Bar */}
            <div className="flex items-center space-x-1.5 text-xs font-mono">
              <span className="text-slate-500 text-[10px] uppercase">Cross-Suite:</span>
              <a
                href={buildSuiteCrossUrl('https://map.aixenergy.io', { project: dossier.canonical_project_id })}
                target="_blank"
                rel="noreferrer"
                className="px-2 py-1 rounded bg-[#121a2d] hover:bg-[#1a253b] text-slate-300 hover:text-[#2979ff] text-[11px] flex items-center transition border border-[#1a253b]"
                title="Open campus geospatial boundary in AIxEnergy Map"
              >
                <span>Map</span>
                <ExternalLink className="w-3 h-3 ml-1 opacity-70" />
              </a>

              <a
                href={buildSuiteCrossUrl('https://monitor.aixenergy.io', { request: radar?.queue_request_id || '' })}
                target="_blank"
                rel="noreferrer"
                className="px-2 py-1 rounded bg-[#121a2d] hover:bg-[#1a253b] text-slate-300 hover:text-[#2979ff] text-[11px] flex items-center transition border border-[#1a253b]"
                title="Open interconnection queue telemetry in AIxEnergy Monitor"
              >
                <span>Monitor</span>
                <ExternalLink className="w-3 h-3 ml-1 opacity-70" />
              </a>

              <a
                href={buildSuiteCrossUrl('https://capital.aixenergy.io', { project: dossier.canonical_project_id })}
                target="_blank"
                rel="noreferrer"
                className="px-2 py-1 rounded bg-[#121a2d] hover:bg-[#1a253b] text-slate-300 hover:text-[#2979ff] text-[11px] flex items-center transition border border-[#1a253b]"
                title="Open capital stack in AIxEnergy Capital"
              >
                <span>Capital</span>
                <ExternalLink className="w-3 h-3 ml-1 opacity-70" />
              </a>
            </div>
          </div>
        </div>

        {/* 2. Content Body */}
        <div className="p-5 space-y-6 flex-1">
          {/* Headline Transaction Summary */}
          {tx && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-[#090e1a] border border-[#1a253b]">
                <div className="text-[10px] uppercase font-mono text-slate-400">Total Consideration</div>
                <div className="text-base font-bold font-mono text-white mt-0.5">
                  {tx.total_consideration_usd
                    ? `$${(tx.total_consideration_usd / 1_000_000).toFixed(1)}M`
                    : 'Non-Disclosure (TX)'}
                </div>
                <div className="text-[10px] text-slate-500 font-mono mt-0.5">Closed: {tx.closing_date}</div>
              </div>

              <div className="p-3 rounded-lg bg-[#090e1a] border border-[#1a253b]">
                <div className="text-[10px] uppercase font-mono text-slate-400">Headline Capacity</div>
                <div className="text-base font-bold font-mono text-white mt-0.5">
                  {tx.headline_capacity_mw ? `${tx.headline_capacity_mw} MW` : 'N/A'}
                </div>
                <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                  {tx.headline_acreage ? `${tx.headline_acreage} Acres` : 'Acreage unstated'}
                </div>
              </div>

              <div className="p-3 rounded-lg bg-[#090e1a] border border-[#1a253b]">
                <div className="text-[10px] uppercase font-mono text-emerald-400">Powered Land $/Acre</div>
                <div className="text-base font-bold font-mono text-emerald-400 mt-0.5">
                  {tx.implied_price_per_acre_usd
                    ? `$${(tx.implied_price_per_acre_usd / 1_000).toFixed(0)}k/ac`
                    : '—'}
                </div>
                <div className="text-[10px] text-slate-500 font-mono mt-0.5">Raw Dirt Multiplier</div>
              </div>

              <div className="p-3 rounded-lg bg-[#090e1a] border border-[#1a253b]">
                <div className="text-[10px] uppercase font-mono text-cyan-400">Power Delivery $/MW</div>
                <div className="text-base font-bold font-mono text-cyan-400 mt-0.5">
                  {tx.implied_price_per_mw_usd
                    ? `$${(tx.implied_price_per_mw_usd / 1_000).toFixed(0)}k/MW`
                    : '—'}
                </div>
                <div className="text-[10px] text-slate-500 font-mono mt-0.5">Interconnect Premium</div>
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* DIRECT SOURCE MATERIAL & REGULATORY PROVENANCE (The requested capability) */}
          {/* ========================================================================= */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2 border-b border-[#1a253b] pb-2">
              <ShieldCheck className="w-4 h-4 text-[#2979ff]" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-200">
                Direct Primary Source Material &amp; Evidence Records
              </h3>
            </div>

            {/* 1. County Recorded Deeds */}
            <div className="space-y-2">
              <div className="text-xs font-semibold text-amber-400 uppercase tracking-wider flex items-center justify-between">
                <span>1. County Recorded Deeds &amp; Conveyance Instruments ({deeds.length})</span>
                <span className="text-[11px] font-mono text-slate-500 font-normal">Official Land Clerk Registry</span>
              </div>

              {deeds.length > 0 ? (
                deeds.map((deed) => {
                  const portal = getCountyRecorderUrl(deed.county_name, deed.state_code, deed.instrument_number);
                  return (
                    <div
                      key={deed.deed_id}
                      className="p-3.5 rounded-lg bg-[#090e1a] border border-[#1a253b] space-y-2 text-xs"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-semibold text-white flex items-center space-x-2">
                            <span>{deed.document_type || 'SPECIAL WARRANTY DEED'}</span>
                            <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">
                              {deed.county_name} County, {deed.state_code}
                            </span>
                          </div>
                          <div className="text-slate-400 text-[11px] mt-0.5 font-mono">
                            Recorded: <b className="text-slate-200">{deed.recording_date}</b>
                            {deed.instrument_number && (
                              <span> &bull; Instrument: <b className="text-white">{deed.instrument_number}</b></span>
                            )}
                            {deed.book_number && (
                              <span> &bull; Book: <b>{deed.book_number}</b> / Page: <b>{deed.page_number}</b></span>
                            )}
                          </div>
                        </div>

                        {/* Direct Portal Link */}
                        <a
                          href={portal.url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center space-x-1 px-2.5 py-1 rounded bg-[#121a2d] hover:bg-[#1a253b] text-amber-400 border border-amber-500/30 text-xs font-mono font-semibold transition shrink-0"
                          title="Open official County Recorder / Land Records portal"
                        >
                          <span>{portal.badgeText}</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>

                      {/* Parties */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] font-mono pt-1 border-t border-[#1a253b]">
                        <div>
                          <span className="text-slate-500">Grantor (Seller):</span>{' '}
                          <span className="text-slate-200">{deed.grantor_raw || 'Private Landowner SPV'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Grantee (Buyer):</span>{' '}
                          <span className="text-slate-200 font-semibold">{deed.grantee_raw || 'Amazon / Microsoft SPV'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Conveyed Acreage:</span>{' '}
                          <span className="text-slate-200">{deed.conveyed_acreage ? `${deed.conveyed_acreage} acres` : 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Stated Consideration:</span>{' '}
                          <span className="text-white">
                            {deed.is_non_disclosure_state
                              ? 'Texas Non-Disclosure ($10 & OVC)'
                              : deed.stated_consideration_usd
                              ? `$${deed.stated_consideration_usd.toLocaleString()}`
                              : 'Not Stated on Face'}
                          </span>
                        </div>
                      </div>

                      {/* Cryptographic SHA-256 Provenance */}
                      <div className="flex items-center justify-between text-[10px] font-mono text-slate-500 pt-1 border-t border-[#1a253b]/60">
                        <span className="truncate max-w-[400px]">SHA-256: {deed.evidence_sha256}</span>
                        <button
                          onClick={() => handleCopyHash(deed.evidence_sha256)}
                          className="text-[#2979ff] hover:text-white flex items-center space-x-1 shrink-0 ml-2"
                          title="Copy SHA-256 cryptographic provenance hash"
                        >
                          {copiedHash === deed.evidence_sha256 ? (
                            <>
                              <Check className="w-3 h-3 text-emerald-400" />
                              <span className="text-emerald-400">Copied</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3" />
                              <span>Copy Hash</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-3 rounded-lg bg-[#090e1a] border border-[#1a253b] text-slate-500 text-xs italic">
                  Public deed conveyance in verification pipeline.
                </div>
              )}
            </div>

            {/* 2. Wholesale Colocation Leases & SEC Filings */}
            <div className="space-y-2">
              <div className="text-xs font-semibold text-cyan-400 uppercase tracking-wider flex items-center justify-between">
                <span>2. Wholesale Colocation Leases &amp; SEC EDGAR Filings ({leases.length})</span>
                <span className="text-[11px] font-mono text-slate-500 font-normal">Public Disclosures</span>
              </div>

              {leases.length > 0 ? (
                leases.map((lease) => {
                  const sec = getSecEdgarUrl(lease.source_sec_cik, lease.tenant_organization_id);
                  return (
                    <div
                      key={lease.lease_id}
                      className="p-3.5 rounded-lg bg-[#090e1a] border border-[#1a253b] space-y-2 text-xs"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-semibold text-white flex items-center space-x-2">
                            <span>{lease.tenant_organization_id.replace('AOI-', '')}</span>
                            <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/30">
                              {lease.lease_structure}
                            </span>
                          </div>
                          <div className="text-slate-400 text-[11px] mt-0.5 font-mono">
                            Contracted: <b className="text-white">{lease.contracted_it_mw} MW IT Load</b> &bull; Term: <b className="text-white">{lease.lease_term_years} Years</b>
                          </div>
                        </div>

                        {/* Direct SEC EDGAR Link */}
                        <a
                          href={sec.url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center space-x-1 px-2.5 py-1 rounded bg-[#121a2d] hover:bg-[#1a253b] text-cyan-400 border border-cyan-500/30 text-xs font-mono font-semibold transition shrink-0"
                          title="Open SEC EDGAR 10-K / 10-Q filing browse"
                        >
                          <span>{sec.badgeText}</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] font-mono pt-1 border-t border-[#1a253b]">
                        <div>
                          <span className="text-slate-500">Rent Rate:</span>{' '}
                          <span className="text-cyan-400 font-bold">${lease.rent_rate_usd_kw_month}/kW-mo</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Escalator:</span>{' '}
                          <span className="text-white">{lease.annual_escalator_pct}%/yr</span>
                        </div>
                        <div>
                          <span className="text-slate-500">PUE Ceiling:</span>{' '}
                          <span className="text-white">{lease.pue_target_ceiling || '1.20'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Pass-Through:</span>{' '}
                          <span className="text-white">{lease.power_pass_through_mechanism || 'TRIPLE_NET'}</span>
                        </div>
                      </div>

                      {/* SHA-256 Provenance */}
                      <div className="flex items-center justify-between text-[10px] font-mono text-slate-500 pt-1 border-t border-[#1a253b]/60">
                        <span className="truncate max-w-[400px]">SHA-256: {lease.evidence_sha256}</span>
                        <button
                          onClick={() => handleCopyHash(lease.evidence_sha256)}
                          className="text-[#2979ff] hover:text-white flex items-center space-x-1 shrink-0 ml-2"
                        >
                          {copiedHash === lease.evidence_sha256 ? (
                            <span className="text-emerald-400">Copied</span>
                          ) : (
                            <>
                              <Copy className="w-3 h-3" />
                              <span>Copy Hash</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-3 rounded-lg bg-[#090e1a] border border-[#1a253b] text-slate-500 text-xs italic">
                  Private single-tenant hyperscale agreement. No public SEC disclosure filed.
                </div>
              )}
            </div>

            {/* 3. Statutory Municipal PILOT Incentives */}
            <div className="space-y-2">
              <div className="text-xs font-semibold text-emerald-400 uppercase tracking-wider flex items-center justify-between">
                <span>3. Statutory Municipal PILOT Agreements &amp; Abatements ({pilots.length})</span>
                <span className="text-[11px] font-mono text-slate-500 font-normal">State Economic Codes</span>
              </div>

              {pilots.length > 0 ? (
                pilots.map((pilot) => {
                  const stat = getStatutoryAuthorityUrl(pilot.statutory_authority, pilot.jurisdiction_name, dossier.state);
                  return (
                    <div
                      key={pilot.incentive_id}
                      className="p-3.5 rounded-lg bg-[#090e1a] border border-[#1a253b] space-y-2 text-xs"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-semibold text-white flex items-center space-x-2">
                            <span>{pilot.jurisdiction_name}</span>
                            <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                              {pilot.term_years} YR PILOT
                            </span>
                          </div>
                          <div className="text-slate-400 text-[11px] mt-0.5 font-mono">
                            Approved: <b className="text-white">{pilot.approval_date}</b>
                            {pilot.evidence_ordinance_number && (
                              <span> &bull; Ordinance: <b className="text-white">{pilot.evidence_ordinance_number}</b></span>
                            )}
                          </div>
                        </div>

                        {/* Direct Statute Link */}
                        <a
                          href={stat.url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center space-x-1 px-2.5 py-1 rounded bg-[#121a2d] hover:bg-[#1a253b] text-emerald-400 border border-emerald-500/30 text-xs font-mono font-semibold transition shrink-0"
                          title="Open official statutory law or municipal code"
                        >
                          <span>{stat.badgeText}</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] font-mono pt-1 border-t border-[#1a253b]">
                        <div>
                          <span className="text-slate-500">RE Abatement:</span>{' '}
                          <span className="text-emerald-400 font-bold">{pilot.real_property_abatement_pct}%</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Servers Exempt:</span>{' '}
                          <span className="text-white">{pilot.personal_property_server_exempt ? 'YES' : 'NO'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Sales Tax:</span>{' '}
                          <span className="text-white">{pilot.sales_and_use_tax_exempt ? 'EXEMPT' : 'TAXABLE'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Annual Fee:</span>{' '}
                          <span className="text-white">{pilot.annual_pilot_fee_usd ? `$${(pilot.annual_pilot_fee_usd / 1000).toFixed(0)}k/yr` : 'None'}</span>
                        </div>
                      </div>

                      {/* SHA-256 Provenance */}
                      <div className="flex items-center justify-between text-[10px] font-mono text-slate-500 pt-1 border-t border-[#1a253b]/60">
                        <span className="truncate max-w-[400px]">SHA-256: {pilot.evidence_sha256}</span>
                        <button
                          onClick={() => handleCopyHash(pilot.evidence_sha256)}
                          className="text-[#2979ff] hover:text-white flex items-center space-x-1 shrink-0 ml-2"
                        >
                          {copiedHash === pilot.evidence_sha256 ? (
                            <span className="text-emerald-400">Copied</span>
                          ) : (
                            <>
                              <Copy className="w-3 h-3" />
                              <span>Copy Hash</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-3 rounded-lg bg-[#090e1a] border border-[#1a253b] text-slate-500 text-xs italic">
                  Standard county tax assessment applies. No statutory PILOT contract executed.
                </div>
              )}
            </div>

            {/* 4. ISO Interconnection Queue Position */}
            {radar && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-rose-400 uppercase tracking-wider flex items-center justify-between">
                  <span>4. ISO / RTO Interconnection Queue Telemetry</span>
                  <span className="text-[11px] font-mono text-slate-500 font-normal">Active Queue Position</span>
                </div>

                <div className="p-3.5 rounded-lg bg-[#090e1a] border border-[#1a253b] space-y-2 text-xs">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-white flex items-center space-x-2">
                        <span>Queue Position #{radar.queue_request_id}</span>
                        <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-rose-500/10 text-rose-400 border border-rose-500/30">
                          {radar.distress_classification}
                        </span>
                      </div>
                      <div className="text-slate-400 text-[11px] mt-0.5 font-mono">
                        ISO: <b className="text-white">{radar.iso_rto}</b> &bull; Capacity: <b className="text-white">{radar.capacity_mw} MW</b> &bull; Distress Score: <b className="text-rose-400">{radar.distress_score}/100</b>
                      </div>
                    </div>

                    <a
                      href={getIsoQueuePortalUrl(radar.iso_rto, radar.queue_request_id).url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center space-x-1 px-2.5 py-1 rounded bg-[#121a2d] hover:bg-[#1a253b] text-rose-400 border border-rose-500/30 text-xs font-mono font-semibold transition shrink-0"
                    >
                      <span>{radar.iso_rto} #{radar.queue_request_id}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] font-mono pt-1 border-t border-[#1a253b]">
                    <div>
                      <span className="text-slate-500">COD Slippage:</span>{' '}
                      <span className="text-rose-400 font-bold">{radar.cod_slippage_months} Mo</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Upgrade Escalation:</span>{' '}
                      <span className="text-white">+{radar.upgrade_cost_escalation_pct}%</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Sponsor CCCR:</span>{' '}
                      <span className="text-amber-400">{radar.sponsor_carrying_capacity_ratio?.toFixed(2) || '0.62'}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Est. Buyout:</span>{' '}
                      <span className="text-white">
                        {radar.estimated_buyout_max_usd
                          ? `$${(radar.estimated_buyout_max_usd / 1_000_000).toFixed(1)}M`
                          : 'Market'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Valuation Decomposition Stack */}
          {decomp && (
            <div className="space-y-3 p-4 rounded-xl bg-[#090e1a] border border-[#1a253b]">
              <div className="flex items-center space-x-2">
                <Zap className="w-4 h-4 text-[#2979ff]" />
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-200">
                  Valuation Decomposition Stack
                </h4>
              </div>

              {/* Stacked Ratio Bar */}
              <div className="h-4 w-full bg-[#06090e] rounded-full overflow-hidden flex text-[10px] font-mono font-bold">
                <div
                  style={{ width: `${Math.max(10, decomp.raw_dirt_pct || 0)}%` }}
                  className="bg-slate-700 text-slate-200 flex items-center justify-center truncate px-1"
                >
                  Dirt {decomp.raw_dirt_pct?.toFixed(0)}%
                </div>
                <div
                  style={{ width: `${Math.max(15, decomp.substation_interconnect_pct || 0)}%` }}
                  className="bg-cyan-700 text-cyan-100 flex items-center justify-center truncate px-1"
                >
                  Substation {decomp.substation_interconnect_pct?.toFixed(0)}%
                </div>
                <div
                  style={{ width: `${Math.max(20, decomp.queue_acceleration_premium_pct || 0)}%` }}
                  className="bg-amber-600 text-amber-100 flex items-center justify-center truncate px-1"
                >
                  Queue {decomp.queue_acceleration_premium_pct?.toFixed(0)}%
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs font-mono">
                <div className="p-2.5 rounded bg-[#06090e] border border-[#1a253b]">
                  <div className="text-[10px] text-slate-500 uppercase">Raw Dirt</div>
                  <div className="font-bold text-white mt-0.5">
                    {decomp.raw_dirt_usd ? `$${(decomp.raw_dirt_usd / 1_000_000).toFixed(1)}M` : '—'}
                  </div>
                  <div className="text-[10px] text-slate-500">{decomp.raw_dirt_pct?.toFixed(0)}% of price</div>
                </div>

                <div className="p-2.5 rounded bg-[#06090e] border border-[#1a253b]">
                  <div className="text-[10px] text-cyan-400 uppercase">Substation</div>
                  <div className="font-bold text-cyan-400 mt-0.5">
                    {decomp.substation_interconnect_usd ? `$${(decomp.substation_interconnect_usd / 1_000_000).toFixed(1)}M` : '—'}
                  </div>
                  <div className="text-[10px] text-slate-500">{decomp.voltage_kv || 500}kV cut-set</div>
                </div>

                <div className="p-2.5 rounded bg-[#06090e] border border-[#1a253b]">
                  <div className="text-[10px] text-amber-400 uppercase">Queue Acceleration</div>
                  <div className="font-bold text-amber-400 mt-0.5">
                    {decomp.queue_acceleration_premium_usd ? `$${(decomp.queue_acceleration_premium_usd / 1_000_000).toFixed(1)}M` : '—'}
                  </div>
                  <div className="text-[10px] text-slate-500">Time-to-Power</div>
                </div>
              </div>
            </div>
          )}

          {/* Tenant-Isolated Diligence Notes (Protected by RLS) */}
          <div className="space-y-3 p-4 rounded-xl bg-[#090e1a] border border-amber-500/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Lock className="w-3.5 h-3.5 text-amber-400" />
                <h4 className="text-xs font-bold uppercase tracking-wider text-amber-400">
                  Private Diligence Notes (Tenant Isolated &bull; RLS Protected)
                </h4>
              </div>
              <span className="text-[10px] font-mono text-slate-500">deal_user schema</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase font-mono text-slate-400 block mb-1">
                  Internal Deal Stage
                </label>
                <select
                  value={dealStage}
                  onChange={(e) => setDealStage(e.target.value as any)}
                  className="w-full bg-[#06090e] border border-[#1a253b] rounded-lg px-2.5 py-1.5 text-xs text-white outline-none focus:border-[#2979ff] font-mono"
                >
                  <option value="WATCHLIST">Watchlist (Tracking)</option>
                  <option value="PRELIM_SCREEN">Preliminary Siting Screen</option>
                  <option value="ACTIVE_UNDERWRITE">Active Underwrite (Live)</option>
                  <option value="LOI_SUBMITTED">LOI Submitted to Sponsor</option>
                  <option value="PASSED">Passed / Archived</option>
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase font-mono text-slate-400 block mb-1">
                  Private Commentary
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={privateNotes}
                    onChange={(e) => setPrivateNotes(e.target.value)}
                    placeholder="e.g. Reviewed 500kV cut-set with PJM study engineer. Sponsor seeking $840M..."
                    className="flex-1 bg-[#06090e] border border-[#1a253b] rounded-lg px-3 py-1.5 text-xs text-white placeholder-slate-500 outline-none focus:border-[#2979ff]"
                  />
                  <button
                    onClick={handleSaveNotes}
                    disabled={isSavingNotes}
                    className="px-3 py-1.5 bg-[#2979ff] hover:bg-[#1c68e8] disabled:opacity-50 text-white font-semibold text-xs rounded-lg transition shrink-0"
                  >
                    {isSavingNotes ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Note'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 3. Footer */}
        <div className="p-3.5 border-t border-[#1a253b] bg-[#090e1a] text-xs text-slate-400 flex items-center justify-between font-mono">
          <span>AIxEnergy Studio &bull; Canonical Asset Dossier</span>
          <span>Invariant: Evidence Before Assertion</span>
        </div>
      </div>
    </div>
  );
};
